import { useMutation, useQuery } from '@tanstack/react-query'
import axios from 'axios'

import { toCamelCase } from '../lib/typeHelper'
import { type CoverageType } from './insuranceCoverage'

export interface QAService {
  value: string
  label: string
}

interface QAServicesResponse {
  services: QAService[]
}

interface RunOnboardingResponse {
  id: string
  nextService: string
  targetService: string
}

interface UpdateDotNumberResponse {
  dotNumber: string
}

interface UpdateOrgStatusResponse {
  status: string
}

function getServices(): Promise<QAService[]> {
  return axios
    .get('/api/v1/insure/qa/services/')
    .then((response) => (toCamelCase(response.data) as QAServicesResponse).services)
}

function runOnboarding(targetService: string): Promise<RunOnboardingResponse> {
  return axios
    .post('/api/v1/insure/qa/run-onboarding/', { target_service: targetService })
    .then((response) => toCamelCase(response.data) as RunOnboardingResponse)
}

function updateDotNumber(dotNumber: string): Promise<UpdateDotNumberResponse> {
  return axios
    .post('/api/v1/insure/qa/update-dot-number/', { dot_number: dotNumber })
    .then((response) => toCamelCase(response.data) as UpdateDotNumberResponse)
}

function updateOrgStatus(status: string): Promise<UpdateOrgStatusResponse> {
  return axios
    .post('/api/v1/insure/qa/update-org-status/', { status })
    .then((response) => toCamelCase(response.data) as UpdateOrgStatusResponse)
}

export function useQAServices() {
  return useQuery({
    queryKey: ['QA_SERVICES'],
    queryFn: getServices,
  })
}

export function useRunOnboarding() {
  return useMutation({
    mutationFn: (targetService: string) => runOnboarding(targetService),
  })
}

export function useUpdateDotNumber() {
  return useMutation({
    mutationFn: (dotNumber: string) => updateDotNumber(dotNumber),
  })
}

export function useUpdateOrgStatus() {
  return useMutation({
    mutationFn: (status: string) => updateOrgStatus(status),
  })
}

export const ORG_STATUSES = [
  { value: 'ONBOARDING', label: 'Onboarding' },
  { value: 'QUOTING', label: 'Quoting' },
  { value: 'UNDERWRITING', label: 'Underwriting' },
  { value: 'ACTIVE', label: 'Active' },
] as const

interface SeedActiveFleetPayload {
  email: string
  trucks: number
  trailers: number
  drivers: number
}

interface SeedActiveFleetResponse {
  orgId: string
  orgName: string
  email: string
  reusedOrg: boolean
  trucks: number
  trailers: number
  drivers: number
  policyId: string
}

function seedActiveFleet(payload: SeedActiveFleetPayload): Promise<SeedActiveFleetResponse> {
  return axios
    .post('/api/v1/insure/qa/seed-active-fleet/', {
      email: payload.email,
      trucks: payload.trucks,
      trailers: payload.trailers,
      drivers: payload.drivers,
    })
    .then((response) => toCamelCase(response.data) as SeedActiveFleetResponse)
}

export function useSeedActiveFleet() {
  return useMutation({
    mutationFn: seedActiveFleet,
  })
}

interface CreateTestPolicyPayload {
  orgId: string
  coverages: CoverageType[]
  /** Send the business owners the same request-to-bind + bound emails a real bind sends. */
  notify: boolean
}

interface CreateTestPolicyResponse {
  policyId: string
  policyIdShort: string
  dateStart: string
  dateEnd: string
  coverages: CoverageType[]
}

/** Seeds a bound test policy on the given org (dev/staging only). */
function createTestPolicy(payload: CreateTestPolicyPayload): Promise<CreateTestPolicyResponse> {
  return axios
    .post('/api/v1/insure/qa/create-test-policy/', {
      org_id: payload.orgId,
      coverages: payload.coverages,
      notify: payload.notify,
    })
    .then((response) => toCamelCase(response.data) as CreateTestPolicyResponse)
}

export function useCreateTestPolicy() {
  return useMutation({
    mutationFn: createTestPolicy,
  })
}

export interface ImportCustomerOrg {
  id: string
  name: string
}

export interface ImportCustomerResponse {
  imported: number
  created: number
  updated: number
  merged: string[]
  dropped: string[]
  filesFetched: number
  filesAlreadyPresent: number
  fileErrors: string[]
  sourceEnv: string
  exportedAt: string
  orgs: ImportCustomerOrg[]
}

/** Loads a fixture exported from the customer page into this database. */
function importCustomer(file: File): Promise<ImportCustomerResponse> {
  const formData = new FormData()
  formData.append('file', file)
  return axios
    .post('/api/v1/insure/qa/import-customer/', formData)
    .then((response) => toCamelCase(response.data) as ImportCustomerResponse)
}

export function useImportCustomer() {
  return useMutation({
    mutationFn: importCustomer,
  })
}
